import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabase";
import { deconnecter } from "./deconnexion";
import { lireMemoire, ecrireMemoire, effacerMemoire, deverrouillerMemoire } from "./memoire";
import AppRouter from "./components/AppRouter";

const INACTIVITE_MS = 30 * 60 * 1000; // 30 minutes

export default function App() {
  const [session, setSession] = useState(undefined);
  const [profil, setProfil] = useState(null);
  const navigate = useNavigate();
  const timerInactivite = useRef(null);
  // Utilisateur dont le profil a déjà été demandé : une seule lecture par utilisateur
  // (getSession, INITIAL_SESSION et TOKEN_REFRESHED arrivent coup sur coup).
  const profilDemandeRef = useRef(null);
  const profilGardeRef = useRef(false); // true tant que le profil affiché est la copie

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) demarrerProfil(session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-mot-de-passe", { replace: true });
        return;
      }
      setSession(session);
      if (session) demarrerProfil(session.user.id);
      else { setProfil(null); profilDemandeRef.current = null; profilGardeRef.current = false; effacerMemoire(); }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!session) return;

    function resetTimer() {
      clearTimeout(timerInactivite.current);
      timerInactivite.current = setTimeout(() => {
        deconnecter();
      }, INACTIVITE_MS);
    }

    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timerInactivite.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [session]); // eslint-disable-line

  // Profil : la copie de la dernière visite s'affiche tout de suite, le vrai
  // la remplace dès qu'il arrive. Une seule lecture par utilisateur.
  function demarrerProfil(uid) {
    if (profilDemandeRef.current === uid) return;
    profilDemandeRef.current = uid;
    deverrouillerMemoire();
    const garde = lireMemoire(uid)?.profil;
    if (garde && garde.id === uid) { setProfil(garde); profilGardeRef.current = true; }
    chargerProfil(uid);
  }

  async function chargerProfil(uid, tentative = 0) {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", uid)
      .single();
    // Déconnecté ou autre utilisateur entre-temps : on ne touche à rien.
    if (profilDemandeRef.current !== uid) return;
    if (error && tentative < 3) {
      // Échec transitoire (réseau / token) : on retente avec backoff au lieu de
      // rester bloqué sur "Chargement du profil...".
      if (tentative === 0) { try { await supabase.auth.refreshSession(); } catch { /* ignore */ } }
      setTimeout(() => chargerProfil(uid, tentative + 1), 600 * (tentative + 1));
      return;
    }
    if (data) {
      setProfil(data);
      profilGardeRef.current = false;
      ecrireMemoire(uid, { profil: data });
    } else {
      // Le vrai profil n'a jamais pu être lu. On pourra le redemander au
      // prochain événement de connexion (TOKEN_REFRESHED, SIGNED_IN)…
      profilDemandeRef.current = null;
      // …et on ne reste pas sur la copie (statut peut-être changé) : retour à l'attente, comme avant.
      if (profilGardeRef.current) { setProfil(null); profilGardeRef.current = false; }
    }
  }

  const handleProfilUpdate = (updates) => setProfil(prev => {
    const suivant = { ...prev, ...updates };
    if (suivant?.id) ecrireMemoire(suivant.id, { profil: suivant });
    return suivant;
  });

  return (
    <AppRouter
      session={session}
      profil={profil}
      sessionLoading={session === undefined}
      profilLoading={!!session && !profil}
      onProfilUpdate={handleProfilUpdate}
    />
  );
}
